import {
  accountScopeState,
  accountScopeSurface,
  ensureAccountScopeCatalog,
  isPaperLedgerAccount,
  paperAccountsEnabled,
  subscribeAccountScope,
  updateAccountScope,
  visibleScopeAccounts,
} from "./accountScope";
import { markForAccountId } from "./ledgerMarks";
import { bindScrollEdges } from "./scrollEdges";
import type { LedgerAccount } from "./ownLedgerClient";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function summary(selected: string[], paperOn: boolean): string {
  if (!selected.length) return paperOn ? "All paper" : "All";
  if (selected.length === 1) return selected[0];
  return `${selected.length} selected`;
}

function accountsForMenu(): LedgerAccount[] {
  return visibleScopeAccounts()
    .slice()
    .sort((a, b) => String(a.label ?? "").localeCompare(String(b.label ?? "")));
}

/** Keep the Live/Paper control in sync even if a later redraw fails. */
function paintPaperButton(host: HTMLElement, paperOn: boolean): void {
  const btn = host.querySelector<HTMLButtonElement>("button[data-account-scope-paper]");
  if (!btn) return;
  btn.textContent = paperOn ? "Paper" : "Live";
  btn.classList.toggle("is-paper", paperOn);
  btn.classList.toggle("is-live", !paperOn);
  btn.setAttribute("aria-pressed", paperOn ? "true" : "false");
  btn.title = paperOn ? "Show live accounts" : "Show paper accounts";
  host.querySelector(".sfe-account-scope")?.classList.toggle("is-paper", paperOn);
}

function groupedAccounts(accounts: LedgerAccount[]): Array<{ broker: string; accounts: LedgerAccount[] }> {
  const groups = new Map<string, LedgerAccount[]>();
  for (const account of accounts) {
    const broker = account.broker_name || account.broker_id || "Account";
    const rows = groups.get(broker) ?? [];
    rows.push(account);
    groups.set(broker, rows);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([broker, rows]) => ({ broker, accounts: rows }));
}

function chipAccountBadge(account: LedgerAccount): string {
  const mark = markForAccountId(account.account_id);
  const code = (mark?.brokerCode || "").trim();
  const short = (mark?.accountShort || account.short_name || account.label || "").trim();
  const broker = code
    ? `<span class="sfe-broker-mark" style="color:${esc(mark?.brokerColor || "#8b93a7")}">${esc(code)}</span>`
    : "";
  return `${broker}<span class="sfe-acct-short">${esc(short)}</span>`;
}

function toggleChipAccounts(accountId: string): void {
  if (!accountId) {
    updateAccountScope({ accountIds: [] });
    return;
  }
  const pool = accountsForMenu().map((account) => account.account_id);
  const poolSet = new Set(pool);
  const current = accountScopeState().accountIds.filter((id) => poolSet.has(id));
  let next: string[];
  if (!current.length) next = [accountId];
  else if (current.includes(accountId)) next = current.filter((id) => id !== accountId);
  else next = [...current, accountId];
  updateAccountScope({ accountIds: next });
}

function renderChipScopeBarHtml(compactLabel = false): string {
  const scope = accountScopeState();
  const paperLocked = accountScopeSurface() === "tax";
  const paperOn = !paperLocked && scope.includePaper;
  const picked = new Set(scope.accountIds);
  const allActive = scope.accountIds.length === 0;
  const chips = accountsForMenu()
    .map((account) => {
      const mark = markForAccountId(account.account_id);
      const active = !allActive && picked.has(account.account_id);
      const paper = isPaperLedgerAccount(account);
      const broker = mark?.brokerTitle || account.broker_name || account.broker_id || "";
      const name = account.name || account.label || mark?.accountTitle || "";
      return `<button type="button" class="sfe-mobile-chip sfe-account-scope-chip${paper ? " is-paper" : ""}${active ? " active" : ""}" data-account-scope-account data-value="${esc(account.account_id)}" data-account-broker="${esc(broker)}" data-account-name="${esc(name)}" aria-pressed="${active ? "true" : "false"}">${chipAccountBadge(account)}</button>`;
    })
    .join("");
  const paperTitle = paperLocked
    ? "Paper accounts stay out of Tax"
    : paperOn
      ? "Show live accounts"
      : "Show paper accounts";
  return `<div class="sfe-account-scope sfe-account-scope-chips${paperOn ? " is-paper" : ""}" data-sfe-account-scope>
    <span class="sfe-account-scope-label">${compactLabel ? "Accts" : "Accounts:"}</span>
    <button type="button" class="sfe-account-scope-paper-btn${paperOn ? " is-paper" : " is-live"}" data-account-scope-paper aria-pressed="${paperOn ? "true" : "false"}"${paperLocked ? " disabled" : ""} title="${esc(paperTitle)}">${paperOn ? "Paper" : "Live"}</button>
    <div class="sfe-scroll-edges">
      <div class="sfe-account-scope-chips-scroll sfe-mobile-chip-scroll" role="group" aria-label="Account">
        <button type="button" class="sfe-mobile-chip sfe-account-scope-chip${paperOn ? " is-paper" : ""}${allActive ? " active" : ""}" data-account-scope-account data-value="" aria-pressed="${allActive ? "true" : "false"}">All</button>
        ${chips}
      </div>
    </div>
  </div>`;
}

export function renderAccountScopeBarHtml(
  variant: "menu" | "chips" = "menu",
  compactLabel = false,
): string {
  if (variant === "chips") return renderChipScopeBarHtml(compactLabel);
  const scope = accountScopeState();
  const paperLocked = accountScopeSurface() === "tax";
  const paperOn = !paperLocked && scope.includePaper;
  const accounts = accountsForMenu();
  const picked = new Set(scope.accountIds);
  const selectedLabels = accounts.filter((account) => picked.has(account.account_id)).map((account) => account.label);
  const allChecked = selectedLabels.length === 0;
  const groups = groupedAccounts(accounts)
    .map((group) => {
      const rows = group.accounts
        .map((account) => {
          const checked = picked.has(account.account_id) ? " checked" : "";
          return `<label class="sfe-account-scope-option">
            <input type="checkbox" data-account-scope-option="accounts" value="${esc(account.account_id)}"${checked} />
            <span>${esc(account.label)}</span>
          </label>`;
        })
        .join("");
      return `<p class="sfe-account-scope-group">${esc(group.broker)}</p>${rows}`;
    })
    .join("");
  const paperTitle = paperLocked ? "Paper accounts stay out of Tax" : "Show only paper accounts";
  return `<div class="sfe-account-scope" data-sfe-account-scope>
    <div class="sfe-account-scope-field">
      <button type="button" class="sfe-account-scope-trigger${paperOn ? " is-paper" : ""}" data-account-scope-open="accounts" aria-haspopup="listbox">Accounts: ${esc(summary(selectedLabels, paperOn))}</button>
      <div class="sfe-account-scope-menu" data-account-scope-menu="accounts" hidden>
        <label class="sfe-account-scope-switch" title="${esc(paperTitle)}">
          <input type="checkbox" role="switch" data-account-scope-paper${paperOn ? " checked" : ""}${paperLocked ? " disabled" : ""} aria-label="Paper accs" />
          <span class="sfe-account-scope-switch-ui" aria-hidden="true"></span>
          <span>Paper accs</span>
        </label>
        <label class="sfe-account-scope-option">
          <input type="checkbox" data-account-scope-all${allChecked ? " checked" : ""} />
          <span>All</span>
        </label>
        ${groups || `<p class="sfe-account-scope-empty">None</p>`}
      </div>
    </div>
  </div>`;
}

function selectedAccountIds(host: HTMLElement): string[] {
  return [...host.querySelectorAll<HTMLInputElement>('[data-account-scope-option="accounts"]:checked')].map(
    (input) => input.value,
  );
}

export function bindAccountScopeBar(host: HTMLElement): () => void {
  const chips =
    host.classList.contains("sfe-mobile-account-scope") || host.classList.contains("sfe-web-account-scope");
  let menuOpen = false;
  let holdTimer: number | null = null;
  let holdTip: HTMLElement | null = null;
  let ignoreChipClick = false;
  let edgeCleanup: (() => void) | null = null;
  const hideHoldTip = () => {
    holdTip?.remove();
    holdTip = null;
  };
  const clearHold = () => {
    if (holdTimer != null) window.clearTimeout(holdTimer);
    holdTimer = null;
  };
  const chipTipText = (chip: HTMLElement): string => {
    const broker = chip.dataset.accountBroker?.trim() ?? "";
    const name = chip.dataset.accountName?.trim() ?? "";
    const who = [broker, name].filter(Boolean).join(" — ");
    if (!who) return "";
    const mode = chip.classList.contains("is-paper") ? "Paper" : "Live";
    return `${who} (${mode})`;
  };
  const showHoldTip = (chip: HTMLElement, fromHold: boolean) => {
    const text = chipTipText(chip);
    if (!text) return;
    hideHoldTip();
    const tip = document.createElement("div");
    tip.className = "sfe-account-scope-tip";
    tip.setAttribute("role", "tooltip");
    tip.textContent = text;
    document.body.appendChild(tip);
    const rect = chip.getBoundingClientRect();
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - tip.offsetWidth - 8));
    tip.style.left = `${left}px`;
    tip.style.top = `${Math.min(rect.bottom + 6, window.innerHeight - tip.offsetHeight - 8)}px`;
    holdTip = tip;
    if (fromHold) {
      ignoreChipClick = true;
      window.setTimeout(hideHoldTip, 2600);
    }
  };
  const paint = () => {
    hideHoldTip();
    edgeCleanup?.();
    edgeCleanup = null;
    document.body.classList.toggle("sfe-paper-mode", paperAccountsEnabled());
    host.innerHTML = renderAccountScopeBarHtml(
      chips ? "chips" : "menu",
      host.classList.contains("sfe-mobile-account-scope"),
    );
    paintPaperButton(host, paperAccountsEnabled());
    const scroll = host.querySelector<HTMLElement>(".sfe-account-scope-chips-scroll");
    if (scroll) edgeCleanup = bindScrollEdges(scroll);
    if (!menuOpen) return;
    const menu = host.querySelector<HTMLElement>('[data-account-scope-menu="accounts"]');
    if (menu) menu.hidden = false;
  };
  const onClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target || !host.contains(target)) return;
    const paperBtn = target.closest<HTMLButtonElement>("button[data-account-scope-paper]");
    if (paperBtn) {
      event.preventDefault();
      if (paperBtn.disabled || accountScopeSurface() === "tax") return;
      const nextPaper = !accountScopeState().includePaper;
      paintPaperButton(host, nextPaper);
      updateAccountScope({ includePaper: nextPaper, accountIds: [] });
      return;
    }
    const chip = target.closest<HTMLButtonElement>("[data-account-scope-account]");
    if (chip) {
      event.preventDefault();
      if (ignoreChipClick) {
        ignoreChipClick = false;
        return;
      }
      toggleChipAccounts(chip.dataset.value ?? "");
      return;
    }
    const open = target.closest<HTMLButtonElement>("[data-account-scope-open]");
    if (!open) return;
    event.preventDefault();
    const menu = host.querySelector<HTMLElement>('[data-account-scope-menu="accounts"]');
    const willOpen = Boolean(menu?.hidden);
    if (menu) menu.hidden = !willOpen;
    menuOpen = willOpen;
  };
  const onChange = (event: Event) => {
    const target = event.target as HTMLInputElement | null;
    if (!target || !host.contains(target)) return;
    menuOpen = true;
    if (target.matches("[data-account-scope-paper]")) {
      updateAccountScope({ includePaper: target.checked });
      return;
    }
    if (target.matches("[data-account-scope-all]")) {
      updateAccountScope({ accountIds: [] });
      return;
    }
    if (target.matches('[data-account-scope-option="accounts"]')) {
      updateAccountScope({ accountIds: selectedAccountIds(host) });
    }
  };
  const onDocClick = (event: Event) => {
    const target = event.target as Node | null;
    if (target && host.contains(target)) return;
    menuOpen = false;
    const menu = host.querySelector<HTMLElement>('[data-account-scope-menu="accounts"]');
    if (menu) menu.hidden = true;
  };
  const onPointerDown = (event: PointerEvent) => {
    if (!chips) return;
    const target = event.target as HTMLElement | null;
    const chip = target?.closest<HTMLButtonElement>("[data-account-name]");
    if (!chip || !host.contains(chip)) return;
    clearHold();
    holdTimer = window.setTimeout(() => showHoldTip(chip, true), 450);
  };
  const onPointerOver = (event: PointerEvent) => {
    if (!chips || event.pointerType === "touch") return;
    const chip = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>("[data-account-name]");
    if (!chip || !host.contains(chip)) return;
    const from = event.relatedTarget;
    if (from instanceof Node && chip.contains(from)) return;
    showHoldTip(chip, false);
  };
  const onPointerOut = (event: PointerEvent) => {
    if (event.pointerType === "touch") return;
    const chip = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>("[data-account-name]");
    if (!chip) return;
    const to = event.relatedTarget;
    if (to instanceof Node && chip.contains(to)) return;
    hideHoldTip();
  };
  const onPointerEnd = () => clearHold();
  const onContextMenu = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("[data-account-name]")) event.preventDefault();
  };
  host.addEventListener("click", onClick);
  host.addEventListener("change", onChange);
  host.addEventListener("pointerdown", onPointerDown);
  host.addEventListener("pointerover", onPointerOver);
  host.addEventListener("pointerout", onPointerOut);
  host.addEventListener("pointerup", onPointerEnd);
  host.addEventListener("pointercancel", onPointerEnd);
  host.addEventListener("pointerleave", onPointerEnd);
  host.addEventListener("contextmenu", onContextMenu);
  document.addEventListener("click", onDocClick);
  const unsub = subscribeAccountScope(() => {
    if (host.isConnected) paint();
  });
  paint();
  void ensureAccountScopeCatalog();
  return () => {
    unsub();
    clearHold();
    hideHoldTip();
    edgeCleanup?.();
    document.body.classList.remove("sfe-paper-mode");
    host.removeEventListener("click", onClick);
    host.removeEventListener("change", onChange);
    host.removeEventListener("pointerdown", onPointerDown);
    host.removeEventListener("pointerover", onPointerOver);
    host.removeEventListener("pointerout", onPointerOut);
    host.removeEventListener("pointerup", onPointerEnd);
    host.removeEventListener("pointercancel", onPointerEnd);
    host.removeEventListener("pointerleave", onPointerEnd);
    host.removeEventListener("contextmenu", onContextMenu);
    document.removeEventListener("click", onDocClick);
  };
}
